import React, { useCallback, useEffect, useMemo, useState } from "react";
import AuthService from "../../modules/Admin/services/AuthService";
import { isEmployeeRole, isSuperAdminRole } from "../../routes/roles";

const STORAGE_KEY_BASE = "nf_product_tour_v1_done_uid_";

/**
 * Shown once per user id (localStorage). Explains sidebar areas and key workflows.
 */
export default function FirstLoginTour() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  const steps = useMemo(() => {
    const roleId = AuthService.getLoggedInRoleId();
    const isSa = isSuperAdminRole(roleId);
    const isEmp = isEmployeeRole(roleId);

    const list = [
      {
        title: "Welcome",
        body: "Here's a quick tour of Next Follow Up—leads, pipelines, attendance, and where admins manage the workspace.",
      },
      {
        title: "Dashboard & filters",
        body: "Use the date range and Search on the dashboard to refresh KPI cards, funnel counts, and follow-up shortcuts.",
      },
      {
        title: "Leads workspace",
        body: "Leads is your master list. Assigned Leads is for daily calls, visits, status updates, and property notes tied to prospects.",
      },
      {
        title: "Kanban pipeline",
        body: "Kanban mirrors your sales stages so the team sees workflow at a glance. Admins configure stages under Pipeline Stages.",
      },
    ];

    if (!isEmp) {
      list.push({
        title: "Reports",
        body: "Open Reports for funnel and outcomes over time. Tenant Settings cover holidays, timezone, and workspace basics.",
      });
    }

    if (!isEmp && !isSa) {
      list.push({
        title: "Admin tools",
        body: "Team Attendance and user onboarding appear in your admin sidebar; employees see a slimmer menu focused on their own pipeline.",
      });
    }

    if (isSa) {
      list.push({
        title: "Super Admin",
        body: "Clients, subscriptions, and platform integrations live under the Super Admin menu for multi-agency setup.",
      });
    }

    list.push({
      title: "You're set",
      body: "Explore anytime from the sidebar. You can dismiss this tour with Skip or Finish—it won't show again on this browser profile.",
    });

    return list;
  }, []);

  useEffect(() => {
    const uid = AuthService.getLoggedInUserId();
    if (!uid) return;
    const key = `${STORAGE_KEY_BASE}${uid}`;
    try {
      if (localStorage.getItem(key)) return;
    } catch {
      return;
    }
    setVisible(true);
  }, []);

  const persistDone = useCallback(() => {
    const uid = AuthService.getLoggedInUserId();
    if (!uid) return;
    try {
      localStorage.setItem(`${STORAGE_KEY_BASE}${uid}`, "1");
    } catch {
      /* ignore */
    }
    setVisible(false);
  }, []);

  if (!visible || !steps.length) return null;

  const current = steps[step];
  const isLast = step >= steps.length - 1;

  return (
    <div className="nf-db-modal-backdrop" role="presentation">
      <div
        className="nf-db-modal nf-tour-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="nf-tour-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="nf-db-modal-header">
          <div>
            <h2 id="nf-tour-title" className="nf-db-modal-title">
              {current?.title ?? "Tour"}
            </h2>
            <p className="nf-db-modal-subtitle">
              Step {step + 1} of {steps.length}
            </p>
          </div>
        </div>
        <div className="nf-db-modal-body">
          <p className="nf-tour-body">{current?.body}</p>
        </div>
        <div className="nf-tour-footer">
          <button type="button" className="nf-btn nf-btn-secondary nf-btn-sm" onClick={persistDone}>
            Skip
          </button>
          <div className="nf-tour-footer-actions">
            {step > 0 ? (
              <button
                type="button"
                className="nf-btn nf-btn-secondary nf-btn-sm"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
              >
                Back
              </button>
            ) : null}
            <button
              type="button"
              className="nf-btn nf-btn-primary nf-btn-sm"
              onClick={() => {
                if (isLast) persistDone();
                else setStep((s) => s + 1);
              }}
            >
              {isLast ? "Get started" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
